import { useQuery } from "@tanstack/react-query";
import { CommunityId, ICommunity } from "@sp-crm/core";
import { trpc } from "components/rpc/trpc";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InteractiveTextLink } from "components/ui/interactive-text-link";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import React, { useCallback, useMemo, useState } from "react";

interface AliasRecord {
    id: string;
    name: string;
    lastSeenAt: string | null;
}

interface CommonProps {
    community: ICommunity;
    onRename: (newName: string) => Promise<void> | void;
}

const formatLastSeen = (lastSeenAt: string | null): string | null => {
    if (!lastSeenAt) {
        return null;
    }
    const date = new Date(lastSeenAt);
    if (isNaN(date.getTime())) {
        return null;
    }
    return `Last seen ${date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    })}`;
};

const sortByLastSeenDesc = (aliases: AliasRecord[]): AliasRecord[] => {
    return [...aliases].sort((a, b) => {
        const aT = a.lastSeenAt ? new Date(a.lastSeenAt).getTime() : 0;
        const bT = b.lastSeenAt ? new Date(b.lastSeenAt).getTime() : 0;
        return bT - aT;
    });
};

const useOtherAliases = (community: ICommunity): AliasRecord[] => {
    const trpcClient = trpc.useTRPC();
    const aliasesQuery = useQuery({
        ...trpcClient.evergreen.aliases.listForCommunity.queryOptions({
            communityId: community.id as CommunityId,
        }),
        enabled: Boolean(community.evergreenCommunityId),
    });

    return useMemo(() => {
        const currentName = (community.name || "").trim().toLowerCase();
        const filtered = (aliasesQuery.data || [])
            .filter(a => a.name.trim().toLowerCase() !== currentName)
            .map(a => ({
                id: a.id,
                name: a.name,
                lastSeenAt: a.lastSeenAt ?? null,
            }));
        return sortByLastSeenDesc(filtered);
    }, [aliasesQuery.data, community.name]);
};

interface CommunityAliasesPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    aliases: AliasRecord[];
    currentName: string;
    onUseName: (newName: string) => Promise<void> | void;
}

const CommunityAliasesPanel: React.FC<CommunityAliasesPanelProps> = ({
    isOpen,
    onDismiss,
    aliases,
    currentName,
    onUseName,
}) => {
    const handleUseName = useCallback(
        async (newName: string): Promise<void> => {
            const confirmed = await fancyConfirm(
                "Rename this community?",
                `Change the community name from "${currentName}" to "${newName}"?`,
                "Yes, rename",
                "Cancel",
                { confirmStyle: "question" },
            );
            if (!confirmed) return;
            await onUseName(newName);
            onDismiss();
        },
        [currentName, onUseName, onDismiss],
    );

    return (
        <ResponsivePanel
            isOpen={isOpen}
            onDismiss={onDismiss}
            type={PanelType.medium}
            headerText="Other names">
            <div className="space-y-4">
                <p className="text-sm text-gray-600">
                    Alternate names found for this community in state licensing records.
                    &ldquo;Last seen&rdquo; is the most recent date each name appeared in
                    a public record.
                </p>
                <ul className="divide-y divide-gray-200 border-t border-gray-200">
                    {aliases.map(alias => {
                        const lastSeen = formatLastSeen(alias.lastSeenAt);
                        return (
                            <li
                                key={alias.id}
                                className="flex items-start justify-between gap-x-4 py-3">
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-gray-900 break-words">
                                        {alias.name}
                                    </div>
                                    {lastSeen ? (
                                        <div className="text-xs text-gray-500">
                                            {lastSeen}
                                        </div>
                                    ) : null}
                                </div>
                                <PrimaryButton
                                    type="button"
                                    onClick={() => handleUseName(alias.name)}
                                    className="flex-shrink-0 text-xs">
                                    Use this name
                                </PrimaryButton>
                            </li>
                        );
                    })}
                </ul>
                <div className="flex justify-end pt-2">
                    <SecondaryButton type="button" onClick={onDismiss}>
                        Close
                    </SecondaryButton>
                </div>
            </div>
        </ResponsivePanel>
    );
};

export const CommunityAliasesInline: React.FC<CommonProps> = ({
    community,
    onRename,
}) => {
    const [isPanelOpen, setPanelOpen] = useState(false);
    const otherAliases = useOtherAliases(community);

    if (!community.evergreenCommunityId) return null;
    if (otherAliases.length === 0) return null;

    return (
        <>
            <InteractiveTextLink
                onClick={() => setPanelOpen(true)}
                className="mt-1 block text-xs">
                Choose from {otherAliases.length} alternate{" "}
                {otherAliases.length === 1 ? "name" : "names"}
            </InteractiveTextLink>
            <CommunityAliasesPanel
                isOpen={isPanelOpen}
                onDismiss={() => setPanelOpen(false)}
                aliases={otherAliases}
                currentName={community.name || ""}
                onUseName={onRename}
            />
        </>
    );
};

export const CommunityAliasesHeaderHint: React.FC<CommonProps> = ({
    community,
    onRename,
}) => {
    const [isPanelOpen, setPanelOpen] = useState(false);
    const otherAliases = useOtherAliases(community);

    if (!community.evergreenCommunityId) return null;
    if (otherAliases.length === 0) return null;

    const shown = otherAliases.slice(0, 2);
    const more = otherAliases.length - shown.length;

    return (
        <>
            <InteractiveTextLink
                onClick={() => setPanelOpen(true)}
                className="block max-w-full truncate text-left text-xs md:text-sm">
                <span className="italic">aka </span>
                {shown.map((a, i) => (
                    <React.Fragment key={a.id}>
                        {i > 0 ? ", " : ""}
                        <span>{a.name}</span>
                    </React.Fragment>
                ))}
                {more > 0 ? ` and ${more} more` : null}
            </InteractiveTextLink>
            <CommunityAliasesPanel
                isOpen={isPanelOpen}
                onDismiss={() => setPanelOpen(false)}
                aliases={otherAliases}
                currentName={community.name || ""}
                onUseName={onRename}
            />
        </>
    );
};
